import { FilesetResolver, FaceLandmarker } from '@mediapipe/tasks-vision'
import { Matrix4, Euler } from 'three'
import { config } from '../config'
import { useHeadStore } from '../store/headStore'

type StopFn = () => void

const tmpMat = new Matrix4()
const tmpEuler = new Euler()

export async function startFaceTracker(video: HTMLVideoElement): Promise<StopFn> {
  let stopped = false

  const vision = await FilesetResolver.forVisionTasks(config.tracking.visionWasmBase)

  const faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
    baseOptions: {
      modelAssetPath: config.tracking.faceModel,
      delegate: 'GPU',
    },
    runningMode: 'VIDEO',
    numFaces: 1,
    outputFaceBlendshapes: false,
    outputFacialTransformationMatrixes: true,
  })

  useHeadStore.getState().setActive(true)

  let lastTs = -1
  let lastWallMs = 0

  const tick = (now: number) => {
    if (stopped) return
    // 节流
    if (now - lastWallMs < config.tracking.faceMinIntervalMs) {
      schedule()
      return
    }
    lastWallMs = now

    // video 必须有有效尺寸才能 detect
    if (video.readyState < 2 || video.videoWidth === 0) {
      schedule()
      return
    }
    const ts = Math.max(performance.now(), lastTs + 1)
    lastTs = ts
    try {
      const result = faceLandmarker.detectForVideo(video, ts)
      const m = result?.facialTransformationMatrixes?.[0]
      if (m && m.data) {
        // Mediapipe matrix is row-major; three.js Matrix4 is column-major
        tmpMat.fromArray(m.data).transpose()
        // YXZ Euler: yaw=Y, pitch=X, roll=Z
        tmpEuler.setFromRotationMatrix(tmpMat, 'YXZ')
        // 注意 Mediapipe 的 yaw 符号：用户头向右转通常是 yaw < 0；
        // 我们希望右转 → yaw > 0，与 right side 对应。所以取负。
        const yaw = -tmpEuler.y
        const pitch = tmpEuler.x
        const roll = tmpEuler.z
        useHeadStore.getState().ingest(yaw, pitch, roll)
      }
    } catch {
      // 跳过这帧，继续下一帧
    }
    schedule()
  }

  // 优先用 requestVideoFrameCallback，否则 raf
  const rvfc =
    'requestVideoFrameCallback' in HTMLVideoElement.prototype
      ? (video as any).requestVideoFrameCallback.bind(video)
      : null

  function schedule() {
    if (stopped) return
    if (rvfc) {
      rvfc(() => tick(performance.now()))
    } else {
      requestAnimationFrame(() => tick(performance.now()))
    }
  }

  schedule()

  return () => {
    stopped = true
    useHeadStore.getState().setActive(false)
    try {
      faceLandmarker.close()
    } catch {
      /* ignore */
    }
  }
}
